/** F.6 / F.7 — the grid row that is OPEN IN THE RECORD PANEL: its highlight, and following it.
 *
 * A leaf module (no component, no AG mount) so the wiring is testable against the SHIPPED code:
 * `CrmListGrid` cannot be rendered under jsdom (~24 useCrm hooks + a live AgGridReact), which is the
 * same reason `crmRecordSelection` exists. What lives here is the two decisions AG cannot make for
 * us — which rows carry the active class, and what to repaint/scroll when the open record changes.
 *
 * Spec: `docs/specs/strategic-architecture/iot-grid-active-row-spec.md` §2.4 / §3.
 */
import type { GridApi, IRowNode, RowClassParams, RowClassRules } from 'ag-grid-community';
import styles from './crm.module.css';
import { isProposedRecordRow } from './crmAiProposedRow';

/** The two row classes, named so tests never have to spell a hashed CSS-module identifier. */
export const PROPOSED_ROW_CLASS = styles.proposedRow;
export const ACTIVE_ROW_CLASS = styles.activeRow;

/**
 * D8 + §5.7(a)/(b) + F.6: the row-level lifecycle treatment.
 *
 * - a whole-RECORD proposal (candidate) gets the full-row green tint + green left accent;
 * - the row open in the panel gets the ACTIVE fill (one step darker than that lifecycle's hover) +
 *   a 4px left accent. `.proposedRow.activeRow` composes to the green active swatch in CSS, so one
 *   class carries both lifecycles rather than the rule having to know which it is.
 * - a committed, not-open row carries no class and takes the theme's white / light-grey-hover default.
 *
 * The pinned "add a record" row has no `id` and no `_proposed_record`, so it can never match either.
 *
 * ⚠ Built per-render (a `useMemo` keyed on the open id), NOT a module constant: the active rule has
 * to close over the open entry id. AG evaluates row class rules at ROW RENDER time, so handing it a
 * new rules object does not restyle rows it has already painted — see `syncActiveRow`.
 */
export function buildRowClassRules(activeEntryId: number | null): RowClassRules {
  return {
    [PROPOSED_ROW_CLASS]: (p: RowClassParams) => isProposedRecordRow(p.data),
    [ACTIVE_ROW_CLASS]: (p: RowClassParams) => {
      if (activeEntryId == null) return false;
      const id = (p.data as { id?: unknown } | undefined)?.id;
      return id != null && Number(id) === activeEntryId;
    },
  };
}

/** The slice of `GridApi` this module needs — so a test can hand it a plain object. */
export type ActiveRowGridApi =
  Pick<GridApi, 'getRowNode' | 'redrawRows' | 'ensureNodeVisible' | 'getEditingCells'>;

/**
 * §2.4: repaint the rows whose active state just changed.
 *
 * ⚠ Kept SEPARATE from the scroll below, and that separation is a review finding, not tidiness. The
 * two have different triggers: a class can be lost to any grid rebuild, so the redraw legitimately
 * re-runs on `gridRev`; a scroll must fire ONLY when the open record genuinely changes, or the grid
 * yanks back to the active row every time the operator sorts, filters, resizes a column or the data
 * refetches — after they deliberately scrolled somewhere else.
 *
 * Two deliberate narrownesses in the redraw itself:
 *
 * 1. **A targeted two-node redraw, never `redrawRows()` with no argument.** A full redraw repaints
 *    the whole viewport with `animateRows` on — correct but needlessly expensive on every ‹ › press.
 *    `getRowId` is configured on the grid, so `getRowNode(String(id))` resolves a node by entry id
 *    even when it is scrolled out of view; row virtualisation is therefore not a problem here.
 *    Redrawing the OUTGOING node is the load-bearing half: without it the highlight stays stuck on
 *    the row you navigated away from and two rows look open at once.
 * 2. **Never while a cell is being edited.** `redrawRows` DESTROYS and recreates the row ctrl, which
 *    takes the open editor and the half-typed value with it — the `refreshCells({force:true})` path
 *    this replaced could not do that. Losing a highlight repaint is cosmetic; losing an operator's
 *    keystrokes is not, so the edit wins. In practice this only bites on a `gridRev`-triggered
 *    re-run: the nav keys are already off while `.ag-cell-inline-editing` is mounted, and clicking a
 *    name cell stops the edit first.
 */
export function redrawActiveRows(
  api: ActiveRowGridApi | null | undefined,
  prevId: number | null,
  nextId: number | null,
): void {
  if (!api) return;
  if (api.getEditingCells?.()?.length) return;
  const ids = prevId != null && prevId !== nextId ? [prevId, nextId] : [nextId];
  const nodes: IRowNode[] = [];
  for (const id of ids) {
    if (id == null) continue;
    const node = api.getRowNode(String(id));
    if (node) nodes.push(node);
  }
  if (nodes.length) api.redrawRows({ rowNodes: nodes });
}

/**
 * §3: scroll the active row into view by the MINIMUM amount. Returns whether the row existed — the
 * caller uses that to keep an owed scroll pending until the row actually arrives (a deep-linked
 * record is opened before its rowData lands), instead of re-scrolling on every rebuild forever.
 *
 * **`ensureNodeVisible(node)` with NO position argument** is AG's minimal scroll: a no-op when the
 * row is already fully visible — which is what makes a plain row CLICK safe — and the least possible
 * movement otherwise. `'middle'` would re-centre the list on every press and read as jumping.
 */
export function scrollActiveRowIntoView(
  api: ActiveRowGridApi | null | undefined,
  activeId: number | null,
): boolean {
  if (!api || activeId == null) return false;
  const node = api.getRowNode(String(activeId));
  if (!node) return false;
  api.ensureNodeVisible(node);
  return true;
}

/** One grid's active-row sync. Call it with the current api + open entry id; it owns the rest. */
export type ActiveRowSync = (api: ActiveRowGridApi | null | undefined, activeId: number | null) => void;

/**
 * The stateful half: WHICH of the two operations above runs on a given call. One instance per grid,
 * created once (`useRef(makeActiveRowSync())`).
 *
 * It lives here rather than inline in `CrmListGrid`'s effect for the reason `crmRecordSelection`
 * documents: the grid cannot be mounted under jsdom, so logic left in the effect can only be tested
 * against a hand-copied replica — and a replica passes while the shipped code diverges. The review
 * finding this closes was *in the effect*, not in either operation, so that is exactly the code that
 * has to be reachable from a test.
 *
 * The rule, and why it is not simply "do both every time":
 *
 * - **Redraw on every call.** Any grid rebuild can drop the class, so the caller is right to re-run
 *   this on `gridRev` / `gridReady` as well as on a record change.
 * - **Scroll only for a genuine active-record change.** `gridRev` also bumps on sort, filter, column
 *   resize/move and every refetch; scrolling on those drags the viewport back to the active row after
 *   the operator deliberately scrolled somewhere else.
 * - **…except while a scroll is still OWED.** A deep-linked record is opened before its row exists,
 *   so the first attempt finds no node. The owed scroll survives until the row arrives and is then
 *   spent exactly once — which is what keeps the deep link working without reintroducing the yank.
 */
export function makeActiveRowSync(): ActiveRowSync {
  let prevId: number | null = null;
  let scrollOwedFor: number | null = null;
  return (api, activeId) => {
    const prev = prevId;
    prevId = activeId;
    if (prev !== activeId) scrollOwedFor = activeId;
    redrawActiveRows(api, prev, activeId);
    if (scrollOwedFor === activeId && scrollActiveRowIntoView(api, activeId)) {
      scrollOwedFor = null;
    }
  };
}
